// The top-bar Help button (owner, 2026-10-25): a compact dropdown like the bell. Search the help
// guides in place (a guide opens in the Help centre), ask root, write to the Adyvance team (open
// tickets counted), how-to videos, ideas, What's new, and the search shortcut.
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { t } from "@ady/i18n";
import { cx, Icon, type IconName } from "@ady/ui";
import { setApi, setKeys } from "../settings/api";
import { TID } from "../testids";
import { AppLink } from "./AppLink";
import { useWhatsNew } from "./ControlCentre";
import { useShell } from "./context";
import { usePopover } from "./usePopover";

export function HelpMenu({ className, onAskRoot }: { className: string; onAskRoot: () => void }) {
  const { membership, orgSlug, storeSlug, openPalette } = useShell();
  const orgId = membership.org.id;
  const { open, setOpen, ref } = usePopover();
  const [q, setQ] = useState("");
  const help = useQuery({
    queryKey: setKeys.help(),
    queryFn: () => setApi.help(),
    enabled: open,
    staleTime: 300_000,
  });
  const tickets = useQuery({
    queryKey: setKeys.tickets(orgId),
    queryFn: () => setApi.tickets(orgId),
    enabled: open,
    retry: false,
  });
  const unseen = useWhatsNew().data?.unseen ?? 0;
  const base = `/${orgSlug}/${storeSlug}/settings/help`;
  const articles = help.data?.data ?? [];
  const guides = useMemo(() => {
    const s = q.trim().toLowerCase();
    return (s ? articles.filter((a) => a.title.toLowerCase().includes(s)) : articles).slice(0, 4);
  }, [articles, q]);
  const openTickets = (tickets.data?.data ?? []).filter((x) => x.state !== "closed").length;
  const close = () => {
    setOpen(false);
    setQ("");
  };
  const rows: {
    icon: IconName;
    label: string;
    badge?: string;
    href?: string;
    onClick?: () => void;
  }[] = [
    { icon: "sparkle", label: t("shell.helpMenu.askRoot"), onClick: onAskRoot },
    {
      icon: "chat",
      label: t("shell.helpMenu.contact"),
      badge: openTickets ? t("shell.helpMenu.openTickets", { count: openTickets }) : undefined,
      href: openTickets ? base : `${base}?ask=1`,
    },
    {
      icon: "graduation",
      label: t("shell.helpMenu.videos"),
      href: `/${orgSlug}/${storeSlug}/services/courses`,
    },
    { icon: "target", label: t("shell.helpMenu.ideas"), href: `${base}?tab=ideas` },
    {
      icon: "gift",
      label: t("shell.helpMenu.whatsNew"),
      badge: unseen ? t("desk.shop.whatsNew.count", { n: unseen }) : undefined,
      href: `${base}?tab=whats-new`,
    },
  ];
  const rowCls =
    "flex w-full items-center gap-2 rounded-sm px-2 py-1 text-start text-xs text-fg no-underline hover:bg-hover";
  return (
    <div className="relative hidden flex-none md:block" ref={ref}>
      <button
        type="button"
        className={cx(className, open && "border-link bg-selected")}
        aria-label={t("shell.help")}
        aria-expanded={open}
        aria-haspopup="dialog"
        data-testid={TID.helpButton}
        onClick={() => (open ? close() : setOpen(true))}
      >
        <Icon name="help" gradient />
      </button>
      {open ? (
        <div
          role="dialog"
          aria-label={t("shell.help")}
          data-testid="help-menu"
          className="absolute end-0 top-full z-(--ady-z-popover) mt-2 flex w-72 max-w-[calc(100vw-1rem)] flex-col rounded-md border border-subtle bg-overlay shadow-2"
        >
          <div className="flex flex-col gap-1.5 border-b border-subtle px-3 py-2">
            <span className="text-xs font-semibold">{t("shell.helpMenu.title")}</span>
            <label className="relative flex items-center">
              <span className="pointer-events-none absolute start-2 text-fg-tertiary">
                <Icon name="search" className="size-3.5" />
              </span>
              <input
                type="search"
                value={q}
                autoFocus
                onChange={(e) => setQ(e.target.value)}
                placeholder={t("shell.helpMenu.searchPh")}
                aria-label={t("shell.helpMenu.searchPh")}
                className="min-h-control w-full rounded-xs border border-control bg-surface ps-7 pe-2 text-xs placeholder:text-fg-tertiary"
                data-testid="help-menu-search"
              />
            </label>
          </div>
          <div className="flex flex-col gap-0.5 border-b border-subtle p-1.5">
            <span className="px-2 pt-0.5 text-2xs font-bold tracking-wide text-fg-tertiary uppercase">
              {q.trim() ? t("shell.helpMenu.results") : t("shell.helpMenu.popular")}
            </span>
            {help.isPending ? (
              <span className="mx-2 my-1 h-12 animate-pulse rounded-xs bg-sunken" />
            ) : guides.length === 0 ? (
              <AppLink
                href={`${base}?ask=1`}
                onClick={close}
                className={cx(rowCls, "text-fg-secondary")}
              >
                <Icon name="info" />
                <span className="min-w-0 flex-1">{t("shell.helpMenu.noMatch")}</span>
              </AppLink>
            ) : (
              guides.map((a) => (
                <AppLink
                  key={a.id}
                  href={`${base}?guide=${encodeURIComponent(a.slug)}`}
                  onClick={close}
                  className={rowCls}
                >
                  <Icon
                    name={a.video_url ? "play" : "file"}
                    className="flex-none text-fg-tertiary"
                  />
                  <span className="min-w-0 flex-1 truncate">{a.title}</span>
                </AppLink>
              ))
            )}
          </div>
          <div className="flex flex-col gap-0.5 border-b border-subtle p-1.5">
            {rows.map((r) => {
              const inner = (
                <>
                  <Icon name={r.icon} gradient className="flex-none" />
                  <span className="min-w-0 flex-1 truncate font-medium">{r.label}</span>
                  {r.badge ? (
                    <span className="flex-none rounded-full bg-danger-bg px-1.5 text-2xs font-semibold text-danger-fg">
                      {r.badge}
                    </span>
                  ) : null}
                </>
              );
              return r.href ? (
                <AppLink key={r.label} href={r.href} onClick={close} className={rowCls}>
                  {inner}
                </AppLink>
              ) : (
                <button
                  key={r.label}
                  type="button"
                  className={rowCls}
                  onClick={() => {
                    close();
                    r.onClick?.();
                  }}
                >
                  {inner}
                </button>
              );
            })}
          </div>
          <div className="flex items-center justify-between gap-2 px-3 py-2">
            <AppLink
              href={base}
              onClick={close}
              className="whitespace-nowrap text-xs font-semibold text-link no-underline hover:underline"
            >
              {t("shell.helpMenu.centre")}
            </AppLink>
            <button
              type="button"
              onClick={() => {
                close();
                openPalette();
              }}
              className="inline-flex items-center gap-1 whitespace-nowrap text-2xs text-fg-tertiary hover:text-fg"
            >
              {t("shell.helpMenu.searchAll")}
              <kbd className="rounded-xs border border-subtle px-1 text-2xs">
                {t("shell.searchShortcut")}
              </kbd>
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
